import React, { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { IndianRupee, Receipt } from 'lucide-react';
import PhoneLedger from './PhoneLedger';
import { useIsMobile } from '@/hooks/useIsMobile';

/**
 * What each contractor owes us in work, and we owe them in money.
 *
 * The tracker next door answers "where is it". This answers the other half of
 * job work: how much material went out with the job, how much came back, and
 * whether the bill matches what was agreed before the work left.
 */

const money = (v) => `₹${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const kg = (v) => `${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} kg`;

function Stat({ label, value, tone }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <p className="text-[10px] uppercase tracking-wider text-slate-400">{label}</p>
      <p className={`text-xl font-bold tabular-nums ${tone || 'text-slate-800'}`}>{value}</p>
    </div>
  );
}

export default function JobWorkLedger() {
  const isMobile = useIsMobile();
  const [data, setData] = useState(null);
  const [unbilledOnly, setUnbilledOnly] = useState(false);
  const [billDialog, setBillDialog] = useState(null);
  const [billForm, setBillForm] = useState({ billed_amount: '', bill_ref: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/external-processing/job-work-ledger', {
        params: { unbilled_only: unbilledOnly },
      });
      setData(res.data);
    } catch {
      toast.error('Could not load the job work ledger');
    }
  }, [unbilledOnly]);

  useEffect(() => { load(); }, [load]);

  const openBill = (row) => {
    setBillDialog(row);
    setBillForm({
      billed_amount: String(row.expected_charge?.amount ?? ''),
      bill_ref: '', notes: '',
    });
  };

  const submitBill = async () => {
    setSaving(true);
    try {
      await api.post('/external-processing/receive-bill', {
        tracking_id: billDialog.tracking_id,
        billed_amount: Number(billForm.billed_amount) || 0,
        bill_ref: billForm.bill_ref,
        notes: billForm.notes,
      });
      toast.success('Bill recorded');
      setBillDialog(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not record the bill');
    }
    setSaving(false);
  };

  if (!data) return <p className="py-10 text-center text-sm text-slate-400">Loading…</p>;

  return (
    <div className="space-y-3" data-testid="job-work-ledger">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Agreed" value={money(data.totals.expected)} />
        <Stat label="Billed" value={money(data.totals.billed)} />
        <Stat label="Variance" value={money(data.totals.variance)}
          tone={data.totals.variance > 0 ? 'text-red-600' : 'text-emerald-600'} />
        <Stat label="Material unaccounted" value={kg(data.totals.unaccounted_kg)}
          tone={data.totals.unaccounted_kg > 0 ? 'text-amber-600' : undefined} />
      </div>

      <Button size="sm" variant={unbilledOnly ? 'default' : 'outline'}
        onClick={() => setUnbilledOnly(v => !v)} data-testid="unbilled-toggle">
        <Receipt className="h-3.5 w-3.5 mr-1" />
        {unbilledOnly ? 'Showing unbilled only' : `Unbilled (${data.totals.unbilled})`}
      </Button>

      {isMobile ? (
        <PhoneLedger rows={data.rows} money={money} kg={kg} onBill={openBill} />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="w-full text-xs" data-testid="ledger-table">
            <thead className="bg-slate-100 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                {['Order', 'Contractor', 'Process', 'Sent', 'Back', 'Short',
                  'Material out', 'Unaccounted', 'Agreed', 'Billed', ''].map(h => (
                  <th key={h} className="whitespace-nowrap px-2.5 py-2 text-left">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.length === 0 ? (
                <tr><td colSpan={11} className="px-2 py-10 text-center text-slate-400">
                  Nothing has gone out to a contractor yet.
                </td></tr>
              ) : data.rows.map(row => (
                <tr key={row.tracking_id} className="border-t border-slate-200"
                  data-testid={`ledger-row-${row.tracking_id}`}>
                  <td className="px-2.5 py-2 font-mono text-[11px]">{row.order_id}</td>
                  <td className="px-2.5 py-2">{row.vendor_name}</td>
                  <td className="px-2.5 py-2 text-slate-500">{row.process || '—'}</td>
                  <td className="px-2.5 py-2 text-right tabular-nums">{row.quantity.sent}</td>
                  <td className="px-2.5 py-2 text-right tabular-nums">
                    {row.quantity.settled ? row.quantity.received : '—'}
                  </td>
                  <td className={`px-2.5 py-2 text-right tabular-nums ${row.quantity.short > 0 ? 'text-red-600 font-semibold' : ''}`}>
                    {row.quantity.settled ? row.quantity.short : '—'}
                  </td>
                  <td className="px-2.5 py-2 text-right tabular-nums text-slate-500">
                    {row.material.issued_kg ? kg(row.material.issued_kg) : '—'}
                  </td>
                  <td className={`px-2.5 py-2 text-right tabular-nums ${row.material.unaccounted_kg > 0 ? 'text-amber-600' : ''}`}>
                    {row.material.issued_kg ? `${kg(row.material.unaccounted_kg)} (${row.material.unaccounted_pct}%)` : '—'}
                  </td>
                  <td className="px-2.5 py-2 text-right tabular-nums">
                    {money(row.expected_charge?.amount)}
                    {row.expected_charge?.minimum_applied && (
                      <Badge className="ml-1 bg-slate-100 text-[9px] text-slate-500">min</Badge>
                    )}
                  </td>
                  <td className="px-2.5 py-2 text-right tabular-nums">
                    {row.bill.billed_received ? (
                      <span className={row.bill.variance > 0 ? 'text-red-600 font-semibold' : ''}>
                        {money(row.bill.billed)}
                        {row.bill.variance !== 0 && (
                          <span className="ml-1 text-[10px]">
                            ({row.bill.variance > 0 ? '+' : ''}{row.bill.variance_pct}%)
                          </span>
                        )}
                      </span>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-2.5 py-2 text-right">
                    {!row.bill.billed_received && (
                      <Button size="sm" variant="outline" className="h-6 text-[10px]"
                        onClick={() => openBill(row)} data-testid={`bill-${row.tracking_id}`}>
                        <IndianRupee className="mr-1 h-3 w-3" /> Bill
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!billDialog} onOpenChange={() => setBillDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record contractor's bill</DialogTitle>
          </DialogHeader>
          {billDialog && (
            <div className="space-y-3">
              <div className="rounded-lg bg-slate-50 p-3 text-sm">
                <p><span className="font-medium">Contractor:</span> {billDialog.vendor_name}</p>
                <p><span className="font-medium">Process:</span> {billDialog.process || '—'}</p>
                <p><span className="font-medium">Agreed:</span> {money(billDialog.expected_charge?.amount)}
                  {billDialog.expected_charge?.rate ? (
                    <span className="text-slate-500">
                      {' '}({money(billDialog.expected_charge.rate)} per {billDialog.expected_charge.per} {billDialog.expected_charge.unit})
                    </span>
                  ) : <span className="text-amber-600"> — no rate was agreed</span>}
                </p>
              </div>
              <div>
                <Label className="text-xs">Billed amount</Label>
                <Input type="number" value={billForm.billed_amount} data-testid="billed-amount"
                  onChange={e => setBillForm(f => ({ ...f, billed_amount: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">Bill reference</Label>
                <Input value={billForm.bill_ref}
                  onChange={e => setBillForm(f => ({ ...f, bill_ref: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">Notes</Label>
                <Input value={billForm.notes}
                  onChange={e => setBillForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBillDialog(null)}>Cancel</Button>
            <Button onClick={submitBill} disabled={saving} data-testid="confirm-bill">
              {saving ? 'Saving…' : 'Record bill'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
